/**
 * Custom hook for managing chart configurations
 * Handles CRUD operations for user's saved charts
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import api from '../lib/api';
import toast from 'react-hot-toast';

// Frontend uses snake_case to match what we receive from API
export interface ChartConfig {
  id: string;
  question_id: string;
  chart_type: string;
  chart_config?: any;
  title?: string;
  sort_order: number;
}

// API expects camelCase in request body
interface ApiChartParams {
  questionId?: string;
  chartType?: string;
  title?: string;
  chartConfig?: any;
}

// Helper to convert snake_case updates to camelCase for API
function toApiFormat(updates: Partial<ChartConfig>): ApiChartParams {
  const apiParams: ApiChartParams = {};
  if (updates.question_id !== undefined) apiParams.questionId = updates.question_id;
  if (updates.chart_type !== undefined) apiParams.chartType = updates.chart_type;
  if (updates.chart_config !== undefined) apiParams.chartConfig = updates.chart_config;
  if (updates.title !== undefined) apiParams.title = updates.title;
  return apiParams;
}

export function useChartConfigs(itemId: string, viewId?: string | null) {
  const queryClient = useQueryClient();
  const queryKey = viewId ? ['analytics-charts', itemId, viewId] : ['analytics-charts', itemId];

  // Fetch charts
  const { data, isLoading, error } = useQuery(
    queryKey,
    async () => {
      const url = viewId
        ? `/outreach/${itemId}/analytics/charts?viewId=${viewId}`
        : `/outreach/${itemId}/analytics/charts`;
      const response = await api.get(url);
      return response.data.data.charts as ChartConfig[];
    },
    {
      enabled: !!itemId,
      staleTime: 5 * 60 * 1000 // 5 minutes
    }
  );

  // Add chart mutation
  const addMutation = useMutation(
    async (params: Partial<ChartConfig>) => {
      // Convert to API format (camelCase)
      const apiParams = { ...toApiFormat(params), ...(viewId ? { viewId } : {}) };
      const response = await api.post(`/outreach/${itemId}/analytics/charts`, apiParams);
      return response.data.data.chart;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        toast.success('Chart added');
      },
      onError: (error: any) => {
        console.error('Failed to add chart:', error);
        toast.error('Failed to add chart');
      }
    }
  );

  // Update chart mutation
  const updateMutation = useMutation(
    async ({ chartId, updates }: { chartId: string; updates: Partial<ChartConfig> }) => {
      // Convert to API format (camelCase)
      const apiParams = toApiFormat(updates);
      const response = await api.put(`/outreach/${itemId}/analytics/charts/${chartId}`, apiParams);
      return response.data.data.chart;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
      },
      onError: (error: any) => {
        console.error('Failed to update chart:', error);
        toast.error('Failed to update chart');
      }
    }
  );

  // Delete chart mutation
  const deleteMutation = useMutation(
    async (chartId: string) => {
      await api.delete(`/outreach/${itemId}/analytics/charts/${chartId}`);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(queryKey);
        toast.success('Chart removed');
      },
      onError: (error: any) => {
        console.error('Failed to delete chart:', error);
        toast.error('Failed to delete chart');
      }
    }
  );

  // Reorder charts mutation. onMutate is synchronous so the cache already
  // holds the new order when the caller's drag state clears — no snap-back.
  const reorderMutation = useMutation(
    async (chartIds: string[]) => {
      await api.put(`/outreach/${itemId}/analytics/charts/reorder`, { chartIds });
    },
    {
      onMutate: (chartIds: string[]) => {
        queryClient.cancelQueries(queryKey);
        const previous = queryClient.getQueryData<ChartConfig[]>(queryKey);
        if (previous) {
          const byId = new Map(previous.map(c => [c.id, c]));
          const reordered = chartIds
            .map(id => byId.get(id))
            .filter((c): c is ChartConfig => !!c);
          previous.forEach(c => {
            if (!byId.has(c.id) || !chartIds.includes(c.id)) reordered.push(c);
          });
          queryClient.setQueryData(queryKey, reordered);
        }
        return { previous };
      },
      onError: (error: any, _chartIds: string[], context: any) => {
        if (context?.previous) {
          queryClient.setQueryData(queryKey, context.previous);
        }
        console.error('Failed to reorder charts:', error);
        toast.error('Failed to reorder charts');
      },
      onSettled: () => {
        queryClient.invalidateQueries(queryKey);
      }
    }
  );

  return {
    charts: data || [],
    isLoading,
    error,
    addChart: (params: Partial<ChartConfig>) => addMutation.mutate(params),
    updateChart: (chartId: string, updates: Partial<ChartConfig>) =>
      updateMutation.mutate({ chartId, updates }),
    deleteChart: deleteMutation.mutate,
    reorderCharts: reorderMutation.mutate,
    isAdding: addMutation.isLoading,
    isUpdating: updateMutation.isLoading,
    isDeleting: deleteMutation.isLoading
  };
}
